import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getTenant } from '../lib/tenant';

type Mode = 'password' | 'magic';

export function Login() {
  const tenant = getTenant();
  const { signIn, signInMagicLink } = useAuth();
  const [mode, setMode] = useState<Mode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);

    if (mode === 'password') {
      const { error: err } = await signIn(email, password);
      setBusy(false);
      if (err) setError(err || 'Sign-in failed');
      // success → AuthContext flips, App router redirects away from /login
    } else {
      const { error: err } = await signInMagicLink(email);
      setBusy(false);
      if (err) setError(err || 'Could not send magic link');
      else setSent(true);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="panel w-full max-w-md">
        <div className="mb-6">
          <div className="text-xs font-medium uppercase tracking-widest text-brand-400">Velocity 9 Admin</div>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight">{tenant.tenant.name}</h1>
          <p className="mt-1 text-sm text-neutral-400">
            {mode === 'password' ? 'Sign in with your email and password.' : 'Sign in with a magic link to your inbox.'}
          </p>
        </div>

        {sent && mode === 'magic' ? (
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-200">
            Check <span className="font-medium">{email}</span> for the sign-in link.
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-neutral-400">Email</label>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourcompany.com.au"
                className="mt-1 w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30"
              />
            </div>

            {mode === 'password' && (
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-neutral-400">Password</label>
                <input
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="mt-1 w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30"
                />
              </div>
            )}

            {error && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200">{error}</div>
            )}

            <button type="submit" disabled={busy} className="btn-primary w-full">
              {busy
                ? mode === 'password' ? 'Signing in…' : 'Sending…'
                : mode === 'password' ? 'Sign in' : 'Send magic link'}
            </button>

            <button
              type="button"
              onClick={() => {
                setMode(mode === 'password' ? 'magic' : 'password');
                setError(null);
                setSent(false);
              }}
              className="w-full text-center text-xs text-neutral-400 underline-offset-2 hover:text-neutral-200 hover:underline"
            >
              {mode === 'password' ? 'or send me a magic link instead' : 'or sign in with password'}
            </button>
          </form>
        )}

        <p className="mt-6 text-xs text-neutral-500">
          Need help? <a href={`mailto:${tenant.tenant.supportEmail}`} className="text-brand-400 hover:underline">{tenant.tenant.supportEmail}</a>
        </p>
        <p className="mt-2 text-xs text-neutral-500">
          Not a client yet? <a href="#shop" className="text-brand-400 hover:underline">Browse the shop →</a>
        </p>
      </div>
    </div>
  );
}
